/**
 * Ready for the tech, shown in the tray BEFORE the drag (#1766, decision 4;
 * threads/new-grid/mockup.html panel 3).
 *
 * The same check the drop runs (#1731, `notReadyForTech` over the job's
 * Finish Line), read lazily: a card asks only once it scrolls into view, so a
 * tray of 40 jobs does not fire 40 reads on load. A failed or forbidden read
 * shows nothing — a check that can't load must never look like a verdict.
 *
 * The card around it takes a dashed orange edge through CSS `:has()` on
 * `[data-not-ready]`, so this component never needs to reach up into the row.
 */
import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { useJobFinishLine } from "@/lib/finish-line";
import { notReadyForTech } from "@/lib/dispatch-preflight";
import { PERSON_INK } from "./state-style";

/** Classes for a tray row so a not-ready job wears the dashed orange edge. */
export const TRAY_NOT_READY_ROW =
    "has-[[data-not-ready]]:border-2 has-[[data-not-ready]]:border-dashed has-[[data-not-ready]]:border-[#C86A00] dark:has-[[data-not-ready]]:border-orange-400";

function useSeen<T extends Element>(): [React.RefObject<T>, boolean] {
    const ref = useRef<T>(null);
    const [seen, setSeen] = useState(false);
    useEffect(() => {
        if (seen) return;
        const el = ref.current;
        if (!el) return;
        if (typeof IntersectionObserver === "undefined") {
            setSeen(true);
            return;
        }
        const io = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setSeen(true);
                    io.disconnect();
                }
            },
            { rootMargin: "120px" },
        );
        io.observe(el);
        return () => io.disconnect();
    }, [seen]);
    return [ref, seen];
}

export function TrayReadyTags({ jobId }: { jobId: string }) {
    const [ref, seen] = useSeen<HTMLDivElement>();
    const q = useJobFinishLine(jobId, seen);
    const list = q.data ? notReadyForTech(q.data) : [];

    if (!seen || q.isLoading) {
        return <div ref={ref} className="h-4" aria-hidden="true" />;
    }
    if (q.isError || !q.data) return <div ref={ref} />;
    if (list.length === 0) {
        return (
            <div
                ref={ref}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300"
                data-testid="tray-ready"
            >
                <Check className="h-3 w-3" strokeWidth={3} /> Ready for the tech
            </div>
        );
    }
    const shown = list.slice(0, 3);
    return (
        <div ref={ref} className="flex flex-wrap gap-1" data-not-ready="" data-testid="tray-not-ready">
            {shown.map((p) => (
                <span
                    key={p.id}
                    title={`${p.title}: ${p.detail}`}
                    className={`rounded-md bg-orange-50 px-1.5 py-0.5 text-[10.5px] font-semibold dark:bg-orange-950 ${PERSON_INK}`}
                >
                    {p.title}
                </span>
            ))}
            {list.length > shown.length && (
                <span className={`text-[10.5px] font-semibold ${PERSON_INK}`}>+{list.length - shown.length} more</span>
            )}
        </div>
    );
}
